import React, { useEffect, useState, useCallback } from "react";
import config from "../config.js";
import pinPrompt from "../utils/pinPrompt";
import openPdfInPrintDialog from "../utils/openPdfInPrintDialog";
import { detailToString } from '../utils/apiError';
import { earmarkStateMeta } from "./EarmarkChip";
import { splitLotUsage } from "../utils/remnantTracking";
import BeaconCard from "./BeaconCard";
import EarmarkSheetsModal from "./EarmarkSheetsModal";
import { mtnUnitCost } from "./MtnUnitCost";
// The lot read is shared with the phone's lot panel (features/materials/lots.js).
import { loadLotDetail, loadLotUsage, loadLotNotes } from '../features/materials/lots';

const API_BASE = config.API_BASE_URL;

/**
 * Shared detail modal for Material Tracking Numbers.
 *
 * Shows MTN header info, usage summary with status badge, consumption history,
 * and the Reconcile / Un-reconcile actions.
 *
 * Props:
 *   mtnId       - numeric ID of the MTN to load (nullable when modal is closed)
 *   show        - boolean, controls modal visibility
 *   onHide      - called when the modal should close
 *   onChanged   - called after a successful reconcile/unreconcile so the parent list can refresh
 */
export default function MtnDetailModal({ mtnId, show, onHide, onChanged }) {
  const [mtn, setMtn] = useState(null);
  const [usage, setUsage] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showAdjustForm, setShowAdjustForm] = useState(false);
  const [adjustRemainingInput, setAdjustRemainingInput] = useState("");
  const [adjustReason, setAdjustReason] = useState("");
  // Notes & warnings (surfaced to the laser operator in the Add-Sheet popup)
  const [mtnNotes, setMtnNotes] = useState([]);
  const [showAddNote, setShowAddNote] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [noteTrigger, setNoteTrigger] = useState("global");    // global | at_sheet | after_sheet
  const [noteSheetNumber, setNoteSheetNumber] = useState("");
  const [noteSeverity, setNoteSeverity] = useState("warning"); // info | warning | critical
  // Earmark sheets on this lot for a sales order (2026-10-13)
  const [showEarmark, setShowEarmark] = useState(false);
  const [beaconTick, setBeaconTick] = useState(0);   // re-read the beacon's screen after an earmark change

  const loadDetail = useCallback(async () => {
    if (!mtnId) return;
    setLoading(true);
    setError(null);
    try {
      const [mtnRes, usageRes] = await Promise.all([
        loadLotDetail(mtnId),
        loadLotUsage(mtnId), // features/materials/lots (the phone's lot panel too)
      ]);
      setMtn(mtnRes);
      setUsage(usageRes);
      setNotes("");
    } catch (err) {
      setError(String(err));
    } finally {
      setLoading(false);
    }
  }, [mtnId]);

  const loadNotes = useCallback(async () => {
    if (!mtnId) return;
    setMtnNotes(await loadLotNotes(mtnId)); // features/materials/lots
  }, [mtnId]);

  useEffect(() => {
    if (show && mtnId) {
      loadDetail();
      loadNotes();
    } else {
      setMtn(null);
      setUsage([]);
      setError(null);
      setNotes("");
      setShowAdjustForm(false);
      setAdjustRemainingInput("");
      setAdjustReason("");
      setMtnNotes([]);
      setShowAddNote(false);
      setNoteText("");
      setNoteTrigger("global");
      setNoteSheetNumber("");
      setNoteSeverity("warning");
      setShowEarmark(false);
    }
  }, [show, mtnId, loadDetail, loadNotes]);

  async function handleReleaseEarmark(e) {
    if (!window.confirm(`Release ${e.order_number}'s earmark? The sheets go back to stock.`)) return;
    let who = null;
    try { who = sessionStorage.getItem("userName"); } catch (x) { /* no session storage */ }
    try {
      const resp = await fetch(`${API_BASE}/purchase_orders/allocations/${e.po_line_allocation_id}/release`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ release_reason: "Released from the tracking number card", released_by_name: who }),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        alert(`Release failed: ${detailToString(body.detail) || resp.statusText}`);
        return;
      }
      await loadDetail();
      setBeaconTick(t => t + 1);
      if (onChanged) onChanged();
    } catch (err) {
      alert(`Release error: ${String(err)}`);
    }
  }

  async function handleReconcile() {
    if (!notes.trim()) {
      alert("Please enter notes describing the investigation before reconciling.");
      return;
    }
    const pin = await pinPrompt("Enter your 4-digit PIN to reconcile this tracking number:");
    if (!pin) {
      return;
    }
    setSubmitting(true);
    try {
      const resp = await fetch(`${API_BASE}/material_tracking/${mtnId}/reconcile`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin, notes: notes.trim() }),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        alert(`Reconcile failed: ${detailToString(body.detail) || resp.statusText}`);
        setSubmitting(false);
        return;
      }
      await loadDetail();
      if (onChanged) onChanged();
    } catch (err) {
      alert(`Reconcile error: ${String(err)}`);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleAdjustUsage() {
    const desiredRemaining = parseInt(adjustRemainingInput, 10);
    if (isNaN(desiredRemaining) || desiredRemaining < 0) {
      alert("Enter a non-negative integer for the correct remaining count.");
      return;
    }
    if (!adjustReason.trim()) {
      alert("Please enter a reason for the adjustment.");
      return;
    }
    const received = mtn?.quantity || 0;
    const rawUsed = mtn?.raw_used_qty ?? mtn?.used_qty ?? 0;
    const movedOut = (mtn?.transferred_out_quantity || 0) + (mtn?.returned_quantity || 0);
    // remaining = received - effective_used - transferred_out - returned, so we want
    // effective_used = received - movedOut - desiredRemaining and
    // usage_adjustment = effective_used - rawUsed
    const newEffectiveUsed = received - movedOut - desiredRemaining;
    const newAdjustment = newEffectiveUsed - rawUsed;
    if (newEffectiveUsed < 0) {
      alert(`Correct remaining (${desiredRemaining}) cannot exceed what this lot still accounts for ` +
            `(received ${received}` + (movedOut ? ` − ${movedOut} transferred/returned` : "") +
            ` = ${received - movedOut}).`);
      return;
    }
    const pin = await pinPrompt(
      `Enter your 4-digit PIN to adjust this tracking number's usage.\n\n` +
      `Received: ${received}\n` +
      (movedOut ? `Transferred out / returned: ${movedOut}\n` : "") +
      `Raw scanned: ${rawUsed}\n` +
      `Adjustment: ${newAdjustment >= 0 ? "+" : ""}${newAdjustment}\n` +
      `New effective used: ${newEffectiveUsed}\n` +
      `New remaining: ${desiredRemaining}`
    );
    if (!pin) return;
    setSubmitting(true);
    try {
      const resp = await fetch(`${API_BASE}/material_tracking/${mtnId}/adjust_usage`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin, adjustment: newAdjustment, reason: adjustReason.trim() }),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        alert(`Adjust failed: ${detailToString(body.detail) || resp.statusText}`);
        setSubmitting(false);
        return;
      }
      setShowAdjustForm(false);
      setAdjustRemainingInput("");
      setAdjustReason("");
      await loadDetail();
      if (onChanged) onChanged();
    } catch (err) {
      alert(`Adjust error: ${String(err)}`);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleClearAdjustment() {
    if (!window.confirm(`Clear the usage adjustment on TRK ${mtn?.tracking_number_display}? The effective used count will revert to the raw scanned count.`)) {
      return;
    }
    const pin = await pinPrompt("Enter your 4-digit PIN to clear the usage adjustment:");
    if (!pin) return;
    setSubmitting(true);
    try {
      const resp = await fetch(`${API_BASE}/material_tracking/${mtnId}/adjust_usage`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin, adjustment: 0, reason: "Adjustment cleared" }),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        alert(`Clear adjustment failed: ${detailToString(body.detail) || resp.statusText}`);
        setSubmitting(false);
        return;
      }
      await loadDetail();
      if (onChanged) onChanged();
    } catch (err) {
      alert(`Clear adjustment error: ${String(err)}`);
    } finally {
      setSubmitting(false);
    }
  }

  async function handlePrintLabel() {
    if (!mtn?.material_id || !mtnId) {
      alert("Material or tracking number missing — can't print.");
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/materials/${mtn.material_id}/label?mtn_id=${mtnId}`);
      if (!res.ok) {
        let errMsg;
        try {
          const body = await res.json();
          errMsg = typeof body.detail === 'string'
            ? body.detail
            : (body.detail && body.detail.message) || JSON.stringify(body.detail || {});
        } catch {
          errMsg = await res.text();
        }
        alert(`Failed to print label: ${errMsg}`);
        return;
      }
      const blob = await res.blob();
      openPdfInPrintDialog(blob);
    } catch (err) {
      alert(`Failed to print label: ${String(err)}`);
    }
  }

  async function handleUnreconcile() {
    if (!window.confirm(`Un-reconcile TRK ${mtn?.tracking_number_display}? This may cause it to reappear in the next daily discrepancy alert.`)) {
      return;
    }
    setSubmitting(true);
    try {
      const resp = await fetch(`${API_BASE}/material_tracking/${mtnId}/unreconcile`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        alert(`Un-reconcile failed: ${detailToString(body.detail) || resp.statusText}`);
        setSubmitting(false);
        return;
      }
      await loadDetail();
      if (onChanged) onChanged();
    } catch (err) {
      alert(`Un-reconcile error: ${String(err)}`);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleAddNote() {
    const text = noteText.trim();
    if (!text) {
      alert("Enter note text.");
      return;
    }
    let sheetNum = null;
    if (noteTrigger === "at_sheet" || noteTrigger === "after_sheet") {
      sheetNum = parseInt(noteSheetNumber, 10);
      if (isNaN(sheetNum) || sheetNum < 1) {
        alert("Enter a sheet number (1 or higher) for this trigger.");
        return;
      }
    }
    const pin = await pinPrompt("Enter your 4-digit PIN to add this note:");
    if (!pin) return;
    setSubmitting(true);
    try {
      const resp = await fetch(`${API_BASE}/material_tracking/${mtnId}/notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pin,
          note_text: text,
          trigger_type: noteTrigger,
          trigger_sheet_number: sheetNum,
          severity: noteSeverity,
        }),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        alert(`Add note failed: ${detailToString(body.detail) || resp.statusText}`);
        setSubmitting(false);
        return;
      }
      setShowAddNote(false);
      setNoteText("");
      setNoteTrigger("global");
      setNoteSheetNumber("");
      setNoteSeverity("warning");
      await loadNotes();
    } catch (err) {
      alert(`Add note error: ${String(err)}`);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResolveNote(noteId) {
    const pin = await pinPrompt("Enter your 4-digit PIN to resolve this note:");
    if (!pin) return;
    setSubmitting(true);
    try {
      const resp = await fetch(`${API_BASE}/material_tracking/${mtnId}/notes/${noteId}/resolve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin }),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        alert(`Resolve failed: ${detailToString(body.detail) || resp.statusText}`);
        setSubmitting(false);
        return;
      }
      await loadNotes();
    } catch (err) {
      alert(`Resolve error: ${String(err)}`);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDeleteNote(noteId) {
    if (!window.confirm("Delete this note permanently?")) return;
    setSubmitting(true);
    try {
      const resp = await fetch(`${API_BASE}/material_tracking/${mtnId}/notes/${noteId}`, { method: "DELETE" });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        alert(`Delete failed: ${detailToString(body.detail) || resp.statusText}`);
        setSubmitting(false);
        return;
      }
      await loadNotes();
    } catch (err) {
      alert(`Delete error: ${String(err)}`);
    } finally {
      setSubmitting(false);
    }
  }

  if (!show) return null;

  // Compute status. remaining_qty and lot_status come from jobq-api's canonical
  // expressions (the same ones the list badge and the status dropdown use): a lot
  // loses scanned usage, the usage adjustment, units transferred to a child lot AND
  // units returned to the owner. The local fallback only covers a stale API mid-deploy.
  const received = mtn?.quantity ?? 0;
  const used = mtn?.used_qty ?? 0;
  const transferredOut = mtn?.transferred_out_quantity ?? 0;
  const returned = mtn?.returned_quantity ?? 0;
  const remaining = mtn?.remaining_qty != null
    ? mtn.remaining_qty
    : received - used - transferredOut - returned;
  const isReconciled = !!mtn?.reconciled_at;
  // 'over' = more left the lot (cut, transferred out, returned) than it received,
  // the same test jobq-api's lot_status uses, not merely used > received.
  const isOverConsumed = remaining < 0;
  const lotStatus = mtn?.lot_status || (
    isReconciled ? "reconciled"
      : isOverConsumed ? "over"
      : remaining <= 0 && returned > 0 ? "returned"
      : remaining <= 0 && transferredOut > 0 ? "transferred"
      : remaining <= 0 ? "exhausted"
      : remaining >= received ? "unused"
      : "active");
  const isExhausted = remaining <= 0 && !isOverConsumed;
  const progressPct = received > 0 ? Math.min(100, Math.round((used / received) * 100)) : 0;

  // Until the lot has loaded there is no status to show: with no data every count above
  // is 0, which reads as "Exhausted" (BCN-003's popup said so for TRK 000469, an active
  // lot with 1 sheet left, 2026-10-12).
  let statusLabel = "Active";
  let statusColor = "success";
  if (!mtn) {
    statusLabel = null;
  } else if (lotStatus === "reconciled") {
    statusLabel = "Reconciled";
    statusColor = "secondary";
  } else if (lotStatus === "over") {
    statusLabel = `Over by ${-remaining}`;
    statusColor = "danger";
  } else if (lotStatus === "returned") {
    statusLabel = "Returned to owner";
    statusColor = "dark";
  } else if (lotStatus === "transferred") {
    statusLabel = "Transferred";
    statusColor = "dark";
  } else if (lotStatus === "exhausted") {
    statusLabel = "Exhausted";
    statusColor = "dark";
  } else if (lotStatus === "unused") {
    statusLabel = "Unused";
    statusColor = "info";
  }

  const sourceIsIntake = mtn?.source_type === "customer_intake";
  const sourceIsTransfer = mtn?.source_type === "transfer";
  // A remnant's own lot (2026-10-14): pieces cut from its parent lot, which they trace through.
  // Its origin decides Customer vs PO (a remnant of Boyd's intake steel is still Boyd's).
  const sourceIsRemnant = mtn?.source_type === "remnant";
  const originIsIntake = sourceIsIntake || (sourceIsRemnant && mtn?.origin_type === "customer_intake");
  const remnantLots = Array.isArray(mtn?.remnant_mtn_ids) ? mtn.remnant_mtn_ids : [];
  const unitCost = mtnUnitCost(mtn);
  const lotOpen = !!mtn && !mtn.reconciled_at && ["unused", "active"].includes(mtn.lot_status)
    && Number(mtn.remaining_qty) > 0;

  return (
    <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header bg-light">
            <h5 className="modal-title">
              <i className="bi bi-upc-scan me-2"></i>
              Tracking Number {mtn?.tracking_number_display || (mtnId ? String(mtnId).padStart(6, "0") : "")}
              {statusLabel && <span className={`badge bg-${statusColor} ms-2`}>{statusLabel}</span>}
            </h5>
            <button type="button" className="btn-close" onClick={onHide} disabled={submitting}></button>
          </div>

          <div className="modal-body">
            {loading && (
              <div className="text-center py-4">
                <div className="spinner-border text-primary"></div>
                <div className="mt-2 text-muted">Loading tracking number details…</div>
              </div>
            )}
            {error && <div className="alert alert-danger">{error}</div>}

            {mtn && !loading && (
              <>
                {/* Info grid */}
                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <div className="card h-100">
                      <div className="card-body">
                        <h6 className="card-subtitle text-muted mb-2">Material</h6>
                        <div className="fw-bold">{mtn.material_code || "—"}</div>
                        <div className="small text-muted">{mtn.material_description || ""}</div>
                        {mtn.material_type && (
                          <div className="small mt-1"><strong>Type:</strong> {mtn.material_type}</div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="card h-100">
                      <div className="card-body">
                        <h6 className="card-subtitle text-muted mb-2">
                          Source
                          <span className={`badge ms-2 bg-${sourceIsRemnant ? "warning text-dark" : sourceIsIntake ? "success" : sourceIsTransfer ? "secondary" : "primary"}`}>
                            {sourceIsRemnant ? "Remnant" : sourceIsIntake ? "Customer Intake" : sourceIsTransfer ? "Transfer" : "PO Receipt"}
                          </span>
                        </h6>
                        {sourceIsRemnant && mtn.parent_mtn_id && (
                          <div className="small mb-1" data-testid="mtn-remnant-parent">
                            <strong>Cut from:</strong> TRK {String(mtn.parent_mtn_id).padStart(6, "0")}
                            {mtn.parent_material_code ? ` (${mtn.parent_material_code})` : ""}
                          </div>
                        )}
                        {originIsIntake ? (
                          <>
                            <div><strong>Customer:</strong> {mtn.customer_name || "—"}</div>
                            {mtn.intake_id && <div className="small"><strong>Intake ID:</strong> {mtn.intake_id}</div>}
                            {sourceIsRemnant && mtn.heat_number && <div className="small"><strong>Heat #:</strong> {mtn.heat_number}</div>}
                          </>
                        ) : (
                          <>
                            <div><strong>PO #:</strong> {mtn.po_number || "—"}</div>
                            <div className="small"><strong>Supplier:</strong> {mtn.supplier || "—"}</div>
                            {mtn.shipment_id && <div className="small"><strong>Shipment ID:</strong> {mtn.shipment_id}</div>}
                            {mtn.heat_number && <div className="small"><strong>Heat #:</strong> {mtn.heat_number}</div>}
                          </>
                        )}
                        <div className="small text-muted mt-1">
                          <strong>{sourceIsRemnant ? "Lot started:" : "Received:"}</strong>{" "}
                          {mtn.created_at ? new Date(mtn.created_at).toLocaleString() : "—"}
                        </div>
                        <div className="small mt-1" data-testid="mtn-unit-cost">
                          <strong>Unit cost:</strong>{" "}
                          {sourceIsRemnant ? (
                            <span className="text-muted">
                              {originIsIntake
                                ? "the customer's own material (no cost to us)"
                                : "priced as a remnant (its share of the sheet), not per lot"}
                            </span>
                          ) : unitCost.isCustomer ? (
                            <span className="text-muted">the customer's own material (no cost to us)</span>
                          ) : unitCost.cost ? (
                            <>
                              {unitCost.cost}
                              <span className="text-muted">
                                {sourceIsTransfer ? " (carried from the source lot)" : mtn.po_number ? ` (PO ${mtn.po_number})` : ""}
                              </span>
                              {unitCost.poNow && (
                                <div className="small text-warning-emphasis">
                                  The PO line is now {unitCost.poNow}: this lot was costed at receipt.
                                </div>
                              )}
                            </>
                          ) : (
                            <span className="text-muted">not recorded</span>
                          )}
                        </div>
                        {Array.isArray(mtn.earmarks) && (mtn.earmarks.length > 0 || !sourceIsIntake || lotOpen) && (
                          <div className="mt-2 pt-2 border-top" data-testid="mtn-earmarks">
                            <div className="small fw-bold d-flex align-items-center">
                              <span className="flex-grow-1"><i className="bi bi-bookmark-fill me-1"></i>Earmarked for</span>
                              {lotOpen && mtn.material_id && (
                                <button type="button" className="btn btn-sm btn-outline-warning py-0"
                                        onClick={() => setShowEarmark(true)}
                                        title="Hold sheets on this pallet for a sales order">
                                  <i className="bi bi-plus-lg me-1"></i>Add earmark
                                </button>
                              )}
                            </div>
                            {mtn.earmarks.length === 0 ? (
                              <div className="small text-muted">None — stock, not held for a sales order.</div>
                            ) : mtn.earmarks.map((e) => {
                              const meta = earmarkStateMeta(e);
                              return (
                                <div key={e.po_line_allocation_id} className="d-flex flex-wrap align-items-center gap-2 small mb-1">
                                  <span className={`badge ${meta.badge}`}>{e.order_number} × {e.quantity}</span>
                                  {e.customer_name && <span>{e.customer_name}</span>}
                                  {e.master_job_num && <span className="text-muted">job {e.master_job_num}</span>}
                                  <span className="text-muted">{meta.text}</span>
                                  {e.source && (
                                    <span className="text-muted fst-italic">
                                      {e.source === "pallet" ? "on this pallet" : `on PO ${mtn.po_number || ""}'s line`}
                                    </span>
                                  )}
                                  {e.state === "open" && (
                                    <button type="button" className="btn btn-link btn-sm p-0 text-danger"
                                            onClick={() => handleReleaseEarmark(e)}>
                                      release
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                            {mtn.earmark_text && (
                              <div className="small text-muted">Label prints: <strong>{mtn.earmark_text}</strong></div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Pallet beacon (renders nothing unless /features turns beacons on) */}
                <BeaconCard key={beaconTick} mtnId={mtnId} />

                <EarmarkSheetsModal
                  show={showEarmark}
                  onHide={() => setShowEarmark(false)}
                  materialId={mtn.material_id}
                  materialCode={mtn.material_code}
                  mtnId={mtn.mtn_id}
                  onChanged={() => { loadDetail(); setBeaconTick(t => t + 1); if (onChanged) onChanged(); }}
                />

                {/* Notes & Warnings */}
                <div className="card mb-3 border-info">
                  <div className="card-header bg-info-subtle d-flex justify-content-between align-items-center">
                    <strong>
                      <i className="bi bi-exclamation-triangle me-1"></i>
                      Notes &amp; Warnings
                    </strong>
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => setShowAddNote(!showAddNote)}
                      disabled={submitting}
                    >
                      <i className="bi bi-plus-lg me-1"></i>{showAddNote ? "Cancel" : "Add Note"}
                    </button>
                  </div>
                  <div className="card-body">
                    <p className="text-muted small mb-2">
                      These appear to the laser operator in the “Add Sheet &amp; Track Material” popup for this tracking number.
                    </p>

                    {showAddNote && (
                      <div className="border rounded p-3 mb-3 bg-light">
                        <div className="mb-2">
                          <label className="form-label small mb-1">Note / warning text</label>
                          <textarea
                            className="form-control form-control-sm"
                            rows={2}
                            placeholder="e.g. Sheet 12 is short (117 3/4 in) — divert to another pallet, do NOT cut."
                            value={noteText}
                            onChange={e => setNoteText(e.target.value)}
                          />
                        </div>
                        <div className="row g-2">
                          <div className="col-md-5">
                            <label className="form-label small mb-1">When should it appear?</label>
                            <select className="form-select form-select-sm" value={noteTrigger} onChange={e => setNoteTrigger(e.target.value)}>
                              <option value="global">Every sheet</option>
                              <option value="at_sheet">A specific sheet #</option>
                              <option value="after_sheet">From a sheet # onward</option>
                            </select>
                          </div>
                          {(noteTrigger === "at_sheet" || noteTrigger === "after_sheet") && (
                            <div className="col-md-3">
                              <label className="form-label small mb-1">Sheet #</label>
                              <input
                                type="number"
                                min={1}
                                className="form-control form-control-sm"
                                placeholder="e.g. 12"
                                value={noteSheetNumber}
                                onChange={e => setNoteSheetNumber(e.target.value)}
                              />
                            </div>
                          )}
                          <div className="col-md-4">
                            <label className="form-label small mb-1">Severity</label>
                            <select className="form-select form-select-sm" value={noteSeverity} onChange={e => setNoteSeverity(e.target.value)}>
                              <option value="info">Info</option>
                              <option value="warning">Warning</option>
                              <option value="critical">Critical (do not use)</option>
                            </select>
                          </div>
                        </div>
                        <div className="mt-2">
                          <button className="btn btn-primary btn-sm" onClick={handleAddNote} disabled={submitting || !noteText.trim()}>
                            {submitting ? "Saving…" : "Save Note"}
                          </button>
                        </div>
                      </div>
                    )}

                    {mtnNotes.length === 0 ? (
                      <div className="text-muted small">No notes on this tracking number.</div>
                    ) : (
                      <div className="d-flex flex-column gap-2">
                        {mtnNotes.map(n => {
                          const sevBadge = n.severity === "critical" ? "danger" : n.severity === "info" ? "info" : "warning";
                          const trigLabel = n.trigger_type === "at_sheet" ? `Piece ${n.trigger_sheet_number}`
                            : n.trigger_type === "after_sheet" ? `From sheet ${n.trigger_sheet_number} on`
                            : "Every sheet";
                          const resolved = !!n.resolved_at;
                          return (
                            <div key={n.note_id} className={`border rounded p-2 ${resolved ? "bg-light text-muted" : ""}`}>
                              <div className="d-flex justify-content-between align-items-start">
                                <div className="small">
                                  <span className={`badge bg-${resolved ? "secondary" : sevBadge} me-2`}>{n.severity}</span>
                                  <span className="badge bg-light text-dark border me-2">{trigLabel}</span>
                                  {resolved && <span className="badge bg-secondary me-2">Resolved</span>}
                                  <div className={`mt-1 ${resolved ? "" : "fw-semibold"}`} style={{ whiteSpace: "pre-wrap" }}>{n.note_text}</div>
                                  <div className="text-muted mt-1" style={{ fontSize: "0.75rem" }}>
                                    {n.created_by_name || "—"}{n.created_at ? ` • ${new Date(n.created_at).toLocaleString()}` : ""}
                                    {resolved && (
                                      <> • Resolved by {n.resolved_by_name || "—"}{n.resolution_note ? `: ${n.resolution_note}` : ""}</>
                                    )}
                                  </div>
                                </div>
                                {!resolved && (
                                  <div className="d-flex gap-1 flex-shrink-0 ms-2">
                                    <button className="btn btn-sm btn-outline-secondary" onClick={() => handleResolveNote(n.note_id)} disabled={submitting} title="Mark resolved">
                                      Resolve
                                    </button>
                                    <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteNote(n.note_id)} disabled={submitting} title="Delete">
                                      <i className="bi bi-trash"></i>
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* Usage summary */}
                <div className="card mb-3">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="mb-0">Usage Summary</h6>
                      <div>
                        <button
                          className="btn btn-sm btn-outline-warning me-2"
                          onClick={() => {
                            setShowAdjustForm(!showAdjustForm);
                            if (!showAdjustForm) setAdjustRemainingInput(String(Math.max(0, remaining)));
                          }}
                          disabled={submitting}
                        >
                          <i className="bi bi-pencil-square me-1"></i>
                          {showAdjustForm ? "Cancel Adjust" : "Adjust Usage"}
                        </button>
                      </div>
                    </div>
                    <div className="small mb-2">
                      <span className="me-3"><strong>Received:</strong> {received}</span>
                      <span className="me-3">
                        <strong>Used:</strong> {used}
                        {mtn.usage_adjustment ? (
                          <span className="text-muted ms-1">
                            ({mtn.raw_used_qty ?? 0} scanned {mtn.usage_adjustment > 0 ? "+" : ""}{mtn.usage_adjustment} adjustment)
                          </span>
                        ) : null}
                        {mtn.remnant_cut_qty ? (
                          <span className="text-muted ms-1" title="Remnant pieces of this lot's sheets, cut later. They carry this heat for traceability and do not count as sheets used.">
                            · {mtn.remnant_cut_qty} remnant cut{mtn.remnant_cut_qty === 1 ? "" : "s"} traced
                          </span>
                        ) : null}
                      </span>
                      {transferredOut > 0 ? (
                        <span className="me-3"><strong>Transferred out:</strong> {transferredOut}</span>
                      ) : null}
                      {remnantLots.length > 0 ? (
                        <span className="me-3" data-testid="mtn-remnant-lots"
                              title="Remnants cut from this lot's sheets hold lots of their own, which carry this heat.">
                          <strong>Remnant lots:</strong>{" "}
                          {remnantLots.map((n) => String(n).padStart(6, "0")).join(", ")}
                        </span>
                      ) : null}
                      {returned > 0 ? (
                        <span className="me-3"><strong>Returned to owner:</strong> {returned}</span>
                      ) : null}
                      <span className={remaining < 0 ? "text-danger" : remaining === 0 ? "text-muted" : ""}>
                        <strong>Remaining:</strong> {remaining}
                      </span>
                    </div>
                    <div className="progress" style={{ height: "20px" }}>
                      <div
                        className={`progress-bar ${isOverConsumed ? "bg-danger" : isExhausted ? "bg-dark" : "bg-success"}`}
                        role="progressbar"
                        style={{ width: `${Math.max(progressPct, isOverConsumed ? 100 : progressPct)}%` }}
                      >
                        {used} / {received}
                      </div>
                    </div>

                    {/* Existing adjustment notice */}
                    {/* !! so an adjustment of 0 renders nothing (a bare 0 && … renders a stray "0") */}
                    {!!mtn.usage_adjustment && !showAdjustForm && (
                      <div className="alert alert-warning mt-3 mb-0 py-2 px-3">
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="small">
                            <strong>
                              <i className="bi bi-pencil-square me-1"></i>
                              Usage Adjustment: {mtn.usage_adjustment > 0 ? "+" : ""}{mtn.usage_adjustment}
                            </strong>
                            <div className="mt-1">
                              <em>{mtn.usage_adjustment_reason || "(no reason)"}</em>
                            </div>
                            <div className="text-muted mt-1">
                              {mtn.usage_adjustment_by_name || (mtn.usage_adjustment_by_user_id ? `User ${mtn.usage_adjustment_by_user_id}` : "System")}
                              {mtn.usage_adjustment_at && ` • ${new Date(mtn.usage_adjustment_at).toLocaleString()}`}
                            </div>
                          </div>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={handleClearAdjustment}
                            disabled={submitting}
                          >
                            Clear
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Adjust form */}
                    {showAdjustForm && (
                      <div className="border border-warning rounded mt-3 p-3 bg-warning-subtle">
                        <div className="small text-muted mb-2">
                          Use this when the physical count on the pallet doesn't match what the system shows —
                          for example, an operator scanned the wrong tracking number, or some usage records got miscredited to this MTN.
                          <strong className="d-block mt-1">This does not change the raw scan history or physical stock quantity.</strong>
                        </div>
                        <div className="row g-2 align-items-end">
                          <div className="col-md-4">
                            <label className="form-label small mb-1">Correct remaining count (physical)</label>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              min={0}
                              max={Math.max(0, received - transferredOut - returned)}
                              value={adjustRemainingInput}
                              onChange={e => setAdjustRemainingInput(e.target.value)}
                            />
                          </div>
                          <div className="col-md-8">
                            <label className="form-label small mb-1">Reason (required)</label>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="e.g. Physical recount — operator scanned wrong MTN on 2 sheets"
                              value={adjustReason}
                              onChange={e => setAdjustReason(e.target.value)}
                            />
                          </div>
                        </div>
                        {/* Preview */}
                        {adjustRemainingInput !== "" && !isNaN(parseInt(adjustRemainingInput, 10)) && parseInt(adjustRemainingInput, 10) >= 0 && parseInt(adjustRemainingInput, 10) <= received - transferredOut - returned && (() => {
                          const want = parseInt(adjustRemainingInput, 10);
                          const effUsed = received - transferredOut - returned - want;
                          const adj = effUsed - (mtn.raw_used_qty ?? 0);
                          return (
                            <div className="small mt-2">
                              Preview: Received {received}
                              {transferredOut + returned > 0 ? ` − ${transferredOut + returned} transferred/returned` : ""}
                              {" "}→ Effective used {effUsed} (adjustment {adj >= 0 ? "+" : ""}{adj}) → Remaining {want}
                            </div>
                          );
                        })()}
                        <div className="mt-2">
                          <button
                            className="btn btn-warning btn-sm"
                            onClick={handleAdjustUsage}
                            disabled={submitting || !adjustReason.trim() || adjustRemainingInput === ""}
                          >
                            {submitting ? "Saving…" : "Save Adjustment"}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Consumption history */}
                <div className="card mb-3">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    {(() => {
                      // Remnant cuts are pieces of sheets this lot already counted: listed for
                      // traceability (what this heat went into), never part of Used.
                      const { sheets, remnantCuts } = splitLotUsage(usage);
                      return (
                        <span>
                          <strong>Consumption History</strong> ({sheets} sheet{sheets === 1 ? "" : "s"}
                          {remnantCuts > 0 ? ` + ${remnantCuts} remnant cut${remnantCuts === 1 ? "" : "s"}` : ""})
                        </span>
                      );
                    })()}
                  </div>
                  <div className="card-body p-0">
                    {usage.length === 0 ? (
                      <div className="text-center text-muted py-3">No sheets recorded against this tracking number yet.</div>
                    ) : (
                      <div style={{ maxHeight: "260px", overflowY: "auto" }}>
                        <table className="table table-sm table-hover mb-0">
                          <thead className="table-light sticky-top">
                            <tr>
                              <th>Date / Time (CT)</th>
                              <th>Job #</th>
                              <th>Station</th>
                              <th>Cut Sheet</th>
                              <th>Recorded By</th>
                            </tr>
                          </thead>
                          <tbody>
                            {usage.map((u, i) => (
                              <tr key={`${u.task_id}_${i}`}>
                                <td>{u.recorded_at ? new Date(u.recorded_at).toLocaleString() : "—"}</td>
                                <td>{u.master_job_num || (u.job_id ? `Job ${u.job_id}` : "—")}</td>
                                <td>{u.workstation || "—"}</td>
                                <td>
                                  {u.cut_sheet_name || "—"}
                                  {u.is_remnant_cut && (
                                    <span
                                      className="badge bg-info-subtle text-info-emphasis ms-1"
                                      title={`Remnant piece of this lot (${u.cut_material_code || "remnant"}). Traced to this heat; not one of the lot's sheets.`}
                                    >
                                      remnant
                                    </span>
                                  )}
                                </td>
                                <td>{u.recorded_by_name || "—"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>

                {/* Reconciliation section */}
                <div className={`card ${isReconciled ? "border-secondary" : "border-warning"}`}>
                  <div className={`card-header ${isReconciled ? "bg-secondary-subtle" : "bg-warning-subtle"}`}>
                    <strong>
                      <i className="bi bi-check2-square me-1"></i>
                      Reconciliation
                    </strong>
                  </div>
                  <div className="card-body">
                    {isReconciled ? (
                      <>
                        <div className="mb-2">
                          <strong>Reconciled at:</strong>{" "}
                          {mtn.reconciled_at ? new Date(mtn.reconciled_at).toLocaleString() : "—"}
                        </div>
                        <div className="mb-2">
                          <strong>Reconciled by:</strong>{" "}
                          {mtn.reconciled_by_name || (mtn.reconciled_by_user_id ? `User ${mtn.reconciled_by_user_id}` : "System")}
                        </div>
                        <div className="mb-3">
                          <strong>Notes:</strong>
                          <div className="bg-light rounded p-2 mt-1">{mtn.reconciliation_notes || <em className="text-muted">(no notes)</em>}</div>
                        </div>
                        <button
                          className="btn btn-outline-secondary btn-sm"
                          onClick={handleUnreconcile}
                          disabled={submitting}
                        >
                          {submitting ? "Working…" : "Un-reconcile"}
                        </button>
                      </>
                    ) : (
                      <>
                        <p className="text-muted small mb-2">
                          Reconciling marks this tracking number as investigated and removes it from the daily discrepancy alert.
                          Provide notes explaining what you found.
                        </p>
                        <textarea
                          className="form-control mb-2"
                          rows={3}
                          placeholder="e.g. Closed — replaced by TRK 000123 which has 10 remaining"
                          value={notes}
                          onChange={e => setNotes(e.target.value)}
                          disabled={submitting}
                        />
                        <button
                          className="btn btn-warning"
                          onClick={handleReconcile}
                          disabled={submitting || !notes.trim()}
                        >
                          {submitting ? "Reconciling…" : "Reconcile"}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-outline-primary me-auto"
              onClick={handlePrintLabel}
              disabled={submitting || !mtn?.material_id}
              title="Print 4×6 material label bound to this MTN"
            >
              <i className="bi bi-printer me-1"></i>Print Label
            </button>
            <button type="button" className="btn btn-secondary" onClick={onHide} disabled={submitting}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
